import { css, html, LitElement, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { FilterButtonVariant } from './types'

@safeCustomElement('mdc-filter-button')
export class MdcFilterButton extends LitElement {
  @property({ type: String }) label = ''
  @property({ type: String }) variant: FilterButtonVariant = 'base'
  @property({ type: Boolean }) bold = false
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) opened = false
  @property({ type: Number }) count = 0
  @property({ type: String }) icon = ''

  static styles = css`
		:host {
			display: inline-block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-filter-button {
			height: 2.5rem;
			font-size: 0.875rem;
			color: var(--color-functional-gray-black, #111111);
			font-weight: 450;
			line-height: 1;
			border: 1px solid var(--color-functional-gray-200, #e5e5e5);
			border-radius: 0.5rem;
			box-sizing: border-box;
			padding: 0.75rem 1rem;
			background-color: var(--color-functional-gray-white, #ffffff);
			transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
			appearance: none;
			border: none;
			cursor: pointer;
			margin: 0;
		}

		.mdc-filter-button--bold {
			font-size: 0.875rem;
			font-weight: 600;
			line-height: 1.2;
			letter-spacing: -0.01em;
		}

		.mdc-filter-button[disabled] {
			cursor: not-allowed;
			color: var(--color-functional-gray-300, #999999);
		}

		.mdc-filter-button:hover:not([disabled]),
		.mdc-filter-button--active:not([disabled]) {
			background-color: var(--color-functional-gray-25, #fafafa);
			border-color: var(--color-functional-gray-black, #111111);
		}

		.mdc-filter-button--dropdown {
			display: flex;
			align-items: center;
			gap: 0.5rem;
		}

		.mdc-filter-button--dropdown__number {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: max-content;
			min-width: 1rem;
			height: max-content;
			min-height: 1rem;
			font-size: 0.625rem;
			color: var(--color-functional-gray-white, #ffffff);
			font-weight: 450;
			line-height: 1;
			text-align: center;
			border-radius: 100%;
			padding: 2px;
			background-color: var(--color-functional-gray-black, #111111);
		}

		.mdc-filter-button--dropdown-arrow {
			flex-shrink: 0;
		}
	`

  render() {
    const classes = [
      'mdc-filter-button',
      this.bold ? 'mdc-filter-button--bold' : '',
      this.variant === 'dropdown' ? 'mdc-filter-button--dropdown' : '',
      this.variant === 'toggle' ? 'mdc-filter-button--toggle' : '',
      this.opened && this.variant === 'dropdown' ? 'mdc-filter-button--opened' : '',
    ]
      .filter(Boolean)
      .join(' ')

    const ariaProps =
      this.variant === 'dropdown'
        ? {
            'aria-expanded': this.opened ? 'true' : 'false',
            'aria-haspopup': 'true',
          }
        : {}

    return html`
			<button type="button" class=${classes} ?disabled=${this.disabled} ...${ariaProps}>
				${this.label}
				${
          this.variant === 'dropdown' && this.count !== undefined
            ? html`<span class="mdc-filter-button--dropdown__number">${this.count}</span>`
            : nothing
        }
				${
          this.variant === 'dropdown'
            ? html`
							<svg
								class="mdc-filter-button--dropdown-arrow"
								width="10"
								height="6"
								viewBox="0 0 10 6"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								aria-hidden="true"
							>
								<path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
							</svg>
						`
            : nothing
        }
				${this.variant === 'toggle' && this.icon ? html`<slot name="icon">${this.icon}</slot>` : nothing}
			</button>
		`
  }
}
